import { useEffect, useRef, useState } from 'react';
import FundoRifa from './FundoRifa';
import { listaNumerosVirgula, pad4, totalDaConta } from '../utils';
import type { PixRifa, TalaoPublico } from '../types';

interface Props {
  numeros: number[];
  pix: PixRifa | null;
  semQR: boolean;
  copiado: boolean;
  aoCopiar: () => void;
  semInternet: boolean;
  tempoEsgotado: boolean;
  verificando: boolean;
  aoVerificar: () => void;
  talao: TalaoPublico;
  expiraEm?: string | null;
  imagemFundo?: string | null;
}

const DURACAO_TOTAL_MS = 30 * 60 * 1000; // 30 minutos

function formatarMmSs(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const min = Math.floor(total / 60);
  const seg = total % 60;
  return `${String(min).padStart(2, '0')}:${String(seg).padStart(2, '0')}`;
}

/** SVG circular que mostra o tempo restante */
function AnelTempo({ restanteMs, totalMs }: { restanteMs: number; totalMs: number }) {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const frac = Math.max(0, Math.min(1, restanteMs / totalMs));
  const offset = circ * (1 - frac);
  const critico = restanteMs < 5 * 60 * 1000;

  return (
    <svg width="88" height="88" viewBox="0 0 88 88" aria-hidden>
      <circle cx="44" cy="44" r={r} fill="none" stroke="#e5e7eb" strokeWidth="6" />
      <circle
        cx="44"
        cy="44"
        r={r}
        fill="none"
        stroke={critico ? '#f59e0b' : '#3b82f6'}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={circ}
        strokeDashoffset={offset}
        style={{ transform: 'rotate(-90deg)', transformOrigin: 'center', transition: 'stroke-dashoffset 1s linear, stroke 0.5s' }}
      />
      <text
        x="44"
        y="44"
        textAnchor="middle"
        dominantBaseline="central"
        fill={critico ? '#92400e' : '#1d4ed8'}
        fontSize="13"
        fontWeight="bold"
        fontFamily="system-ui, sans-serif"
      >
        {formatarMmSs(restanteMs)}
      </text>
    </svg>
  );
}

export default function PassoPix({
  numeros,
  pix,
  semQR,
  copiado,
  aoCopiar,
  semInternet,
  tempoEsgotado,
  verificando,
  aoVerificar,
  talao,
  expiraEm,
  imagemFundo,
}: Props) {
  const [montado, setMontado] = useState(false);
  const [restanteMs, setRestanteMs] = useState(DURACAO_TOTAL_MS);
  const inicioRef = useRef(Date.now());

  useEffect(() => {
    const t = setTimeout(() => setMontado(true), 30);
    return () => clearTimeout(t);
  }, []);

  // Cronômetro regressivo
  useEffect(() => {
    if (semQR || !expiraEm) {
      // sem QR: não temos expira_em, apenas conta do momento que montou
      inicioRef.current = Date.now();
    } else {
      // tenta usar expira_em da compra se disponível
      const expTs = new Date(expiraEm).getTime();
      const agora = Date.now();
      const restante = expTs - agora;
      if (restante > 0) setRestanteMs(restante);
      return;
    }

    const int = window.setInterval(() => {
      setRestanteMs(Math.max(0, DURACAO_TOTAL_MS - (Date.now() - inicioRef.current)));
    }, 1000);
    return () => clearInterval(int);
  }, [semQR, expiraEm]);

  const totalTexto = pix ? totalDaConta(pix.detalhe, pix.total_reais) : null;
  const critico = restanteMs < 5 * 60 * 1000;

  const cardClasses =
    'bg-white/85 backdrop-blur-md rounded-3xl shadow-xl p-5 md:p-6';

  function copiarEVibrar() {
    aoCopiar();
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate?.(30);
    }
  }

  return (
    <FundoRifa imagemUrl={imagemFundo}>
      <div
        className="mx-auto w-full max-w-[560px] px-4 py-6 transition-all duration-[220ms] ease-out"
        style={{
          opacity: montado ? 1 : 0,
          transform: montado ? 'translateY(0)' : 'translateY(12px)',
        }}
      >
        {/* Cabeçalho */}
        <div className="mb-5 text-center">
          <h1 className="text-3xl font-bold text-white drop-shadow-lg">Falta só pagar</h1>
          {totalTexto && (
            <p className="mt-1 text-4xl font-extrabold text-white drop-shadow-lg">{totalTexto}</p>
          )}
        </div>

        {/* Cartão principal: QR + copiar */}
        <div className={`${cardClasses} mb-3`}>
          {semQR ? (
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
                <i className="ri-time-line text-3xl text-amber-600" />
              </div>
              <p className="font-semibold text-foreground-950">Compra aguardando pagamento</p>
              <p className="mt-1 text-sm text-secondary-600">
                Números: {listaNumerosVirgula(numeros)}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile: botão copiar ANTES do QR */}
              <div className="mb-4 md:hidden">
                <button
                  type="button"
                  onClick={copiarEVibrar}
                  disabled={!pix?.pix_copia_e_cola}
                  className={`flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base font-bold transition-all disabled:cursor-not-allowed ${
                    copiado
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30 hover:from-primary-600 hover:to-primary-700'
                  }`}
                >
                  <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} />
                  {copiado ? 'Copiado ✓' : 'Copiar código Pix'}
                </button>
              </div>

              {/* Desktop: QR à esquerda, botão à direita */}
              <div className="hidden items-center gap-6 md:flex">
                <div className="flex-shrink-0">
                  <div className="rounded-2xl border border-secondary-200 bg-white p-3 shadow-sm">
                    <img
                      src={`data:image/png;base64,${pix?.pix_qrcode_base64}`}
                      alt="QR Code Pix"
                      width={180}
                      height={180}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm text-secondary-600">
                    Abra seu banco → Pix → Ler QR Code ou cole o código abaixo
                  </p>
                  <button
                    type="button"
                    onClick={copiarEVibrar}
                    disabled={!pix?.pix_copia_e_cola}
                    className={`mt-3 flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl px-5 py-3 text-sm font-bold transition-all disabled:cursor-not-allowed ${
                      copiado
                        ? 'bg-green-500 text-white'
                        : 'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/30 hover:from-primary-600 hover:to-primary-700'
                    }`}
                  >
                    <i className={copiado ? 'ri-check-line' : 'ri-file-copy-line'} />
                    {copiado ? 'Copiado ✓' : 'Copiar código Pix'}
                  </button>
                </div>
              </div>

              {/* Mobile: QR abaixo do botão */}
              <div className="flex justify-center md:hidden">
                <div className="rounded-2xl border border-secondary-200 bg-white p-3 shadow-sm">
                  <img
                    src={`data:image/png;base64,${pix?.pix_qrcode_base64}`}
                    alt="QR Code Pix"
                    width={200}
                    height={200}
                  />
                </div>
              </div>
              <p className="mt-3 text-center text-xs text-secondary-500 md:hidden">
                Abra o app do seu banco → Pix → Ler QR Code
              </p>
            </>
          )}
        </div>

        {/* Anel de tempo */}
        {!semQR && !tempoEsgotado && (
          <div className={`${cardClasses} mb-3 flex items-center gap-4`}>
            <AnelTempo restanteMs={restanteMs} totalMs={DURACAO_TOTAL_MS} />
            <div>
              <p className={`text-sm font-semibold ${critico ? 'text-amber-700' : 'text-foreground-950'}`}>
                Este código vale por {formatarMmSs(restanteMs)}
              </p>
              <p className="mt-0.5 text-xs text-secondary-500">
                Assim que o banco confirmar, seus números aparecem aqui. Costuma levar segundos.
              </p>
            </div>
          </div>
        )}

        {/* Status de aguardando */}
        {!semQR && !tempoEsgotado && (
          <div className={`${cardClasses} mb-3`}>
            <div className="flex items-center gap-3">
              {semInternet ? (
                <>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-100">
                    <i className="ri-wifi-off-line text-amber-600" />
                  </span>
                  <p className="text-sm text-foreground-800">
                    Sem internet. Vamos continuar tentando assim que a conexão voltar.
                  </p>
                </>
              ) : (
                <>
                  <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-75" style={{ animation: 'aguardandoPulso 2s ease-in-out infinite' }} />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-primary-500" />
                  </span>
                  <p className="text-sm text-foreground-800">Aguardando o seu Pix…</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Números reservados */}
        {numeros.length > 0 && !semQR && (
          <div className={`${cardClasses} mb-3`}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-secondary-500">
              <i className="ri-lock-line mr-1" />
              Reservados para você
            </p>
            <div className="flex flex-wrap gap-2">
              {numeros.map((n) => (
                <span
                  key={n}
                  className="flex items-center gap-1 rounded-lg bg-secondary-100 px-2.5 py-1.5 text-sm font-semibold text-secondary-700"
                >
                  <i className="ri-lock-2-line text-xs" />
                  {pad4(n)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Verificar manualmente */}
        {(tempoEsgotado || semQR) && (
          <div className={`${cardClasses} mb-3 text-center`}>
            {tempoEsgotado && (
              <p className="mb-3 text-sm text-foreground-800">
                O tempo do código Pix chegou ao fim. Se você já pagou, clique abaixo para verificar.
              </p>
            )}
            <button
              type="button"
              onClick={aoVerificar}
              disabled={verificando}
              className="w-full cursor-pointer whitespace-nowrap rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-primary-500/30 transition-all hover:from-primary-600 hover:to-primary-700 disabled:cursor-not-allowed disabled:from-secondary-300 disabled:to-secondary-400 disabled:shadow-none"
            >
              {verificando ? 'Verificando…' : 'Já paguei — verificar'}
            </button>
          </div>
        )}
      </div>

      <style>{`
        @media (prefers-reduced-motion: no-preference) {
          @keyframes aguardandoPulso {
            0%, 100% { opacity: 0.4; }
            50% { opacity: 1; }
          }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes aguardandoPulso {
            0%, 100% { opacity: 1; }
          }
        }
      `}</style>
    </FundoRifa>
  );
}